import { useMemo, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useMount } from "@/Common/CustomHooks/useMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { EmailFormatter } from "@/Common/Utilities/EmailFormatter";
import { Button } from "@/DesignSystem";

import styles from "./VerifyRecoveryEmailPopup.module.scss";

interface IVerifyRecoveryEmailPopupProps {
    email: string;
    contactPointId: string;
    sendCodeOnMount: boolean;
    stringProvider?: ResourceProvider;

    onBack: () => void;
    onVerify: (verificationCode: string) => Promise<ClientResponse>;
    onClose?: () => void;
    showCloseButton?: boolean;
}

function VerifyRecoveryEmailPopup(props: IVerifyRecoveryEmailPopupProps): JSX.Element {
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [isDisabledResendButton, setIsDisabledResendButton] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { getText } = useTranslation(props.stringProvider);

    const emailFormatter = useMemo(() => new EmailFormatter(), []);
    const formattedEmail = useMemo(
        () => props.email ? emailFormatter.formatForPrivacy(props.email) : "",
        [props.email, emailFormatter]
    );
    const disableVerifyEmailButton = IsNullOrWhiteSpace(verificationCode) || isProcessing;

    async function verifyEmail(): Promise<void> {
        if (disableVerifyEmailButton)
            return;

        setIsProcessing(true);
        const response = await props.onVerify(verificationCode);
        setVerificationCode("");

        if (!response.isSuccess)
            setError(response.errors?.[0]?.Message);
        setIsProcessing(false);
    }

    useMount(async () => {
        if (props.sendCodeOnMount) {
            setIsProcessing(true);
            await resendEmailVerificationCode(true);
            setIsProcessing(false);
        }
        else {
            setTimeout(() => setIsDisabledResendButton(false), 3000);
        }
    });

    async function resendEmailVerificationCode(disabledToast: boolean = false): Promise<void> {
        if (isDisabledResendButton)
            return;

        setIsDisabledResendButton(true);
        setIsProcessing(true);

        const response = await Client.postAsync<any>(
            ServerOperations.Server.Myle_Employee_Resend_Verification_Code,
            {
                Address: props.email,
                ContactPointId: props.contactPointId
            }
        );

        if (response?.errors?.any()) {
            sessionStore.showPopup(response.errors.map(p => getText(p.Code)));
        }
        else if (!disabledToast) {
            displayToast(
                MessageToast.Success,
                getText("CodeSentLabel"),
                {
                    hideProgressBar: false,
                    pauseOnFocusLoss: false,
                    autoClose: 3000,
                    pauseOnHover: false,
                    closeOnClick: false
                }
            );
        }

        setIsProcessing(false);
        setTimeout(() => setIsDisabledResendButton(false), 3000);
    }

    function renderFooter(): JSX.Element {
        return (
            <div className={styles.footerContainer}>
                <Button
                    variant="neutral"
                    category="secondary"
                    label={getText("BackLabel")}
                    onClick={props.onBack}
                    dataCy="login-backButton"
                />
                <Button
                    variant="neutral"
                    category="secondary"
                    label={getText("ResendLabel")}
                    onClick={() => resendEmailVerificationCode()}
                    disabled={isDisabledResendButton}
                    dataCy="login-resendButton"
                />
                <Button
                    variant="brand"
                    category="primary"
                    label={getText("VerifyLabel")}
                    onClick={verifyEmail}
                    disabled={disableVerifyEmailButton}
                    dataCy="login-verifyButton"
                />
            </div>
        );
    }

    const verificationCodeId = "verification-code";

    return (
        <Modal
            title={getText("VerifyRecoveryEmailLabel")}
            centerContent
            fixedOverlayPosition
            showOverlay
            showX={props.showCloseButton}
            disableOverlayClose
            footer={renderFooter()}
            dataCy="login-verifyRecoveryEmailPopupControl"
            onClose={props.onClose}
        >
            <div>
                <p className={styles.messageRow}>
                    <strong>{getText("VerifyRecoveryEmailMessage")}</strong>
                </p>
                <p className={styles.messageRow}>
                    {getText("PleaseEnterVerificationCodeSentToEmailMessage", { email: formattedEmail })}
                </p>
                <div className={styles.verificationCodeGrid}>
                    <label
                        htmlFor={verificationCodeId}
                        className={styles.verificationCodeLabel}
                    >
                        {getText("VerificationCodeLabel")}
                    </label>
                    <TextboxControl
                        value={verificationCode}
                        valid={!error}
                        maxLength={50}
                        id={verificationCodeId}
                        onChange={value => {
                            setVerificationCode(value);
                            setError(null);
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter")
                                verifyEmail();
                        }}
                        dataCy="login-verificationCode"
                    />
                    {error && (
                        <>
                            <div />
                            <div className={styles.verificationCodeError} data-cy="login-error">{error}</div>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export { VerifyRecoveryEmailPopup };
